<template>
    <div @click="closeDropdown">
        <Head :title="__('Knowledge Base Categories')" />
        <div class="mb-6 flex justify-end items-center">
            <Link class="btn-indigo" :href="route('knowledge_base_categories.create')">
                <span>{{ __('Create Knowledge Base Category') }}</span>
            </Link>
        </div>

        <div>
            <h1 class="text-2xl font-semibold mb-6">{{ __('Knowledge Base') }}</h1>
            <div class="space-y-4">
                <div v-for="category in categories" :key="category.id" class="p-4 bg-white rounded shadow" @click.stop>
                    <!-- Parent Categories -->
                    <div>
                        <div :href="route('knowledge_base_categories.edit', category.id)" class="text-lg font-bold text-indigo-600">
                            <div class="flex justify-between items-center p-2 rounded">
                                <div>{{ category.name }}</div>
                                <EllipsisVerticalIcon class="w-7 h-7 cursor-pointer" @click.stop="toggle(category.id)"/>
                            </div>
                        </div>
                    </div>
                    <!-- Dropdown Menu for Parent Categories -->
                    <div v-if="activeDropdown === category.id" class="absolute flex flex-col right-4 mt-2 w-48 bg-white border rounded shadow-md z-10">
                        <ul class="p-2">
                            <Link :href="route('knowledge_base_categories.edit', category.id)">
                                <li class="p-2 hover:bg-gray-100 cursor-pointer">{{ __('Edit') }}</li>
                            </Link>
                            <Link :href="route('knowledge_base_categories.create', {parent_id: category.id})">
                                <li class="p-2 hover:bg-gray-100 cursor-pointer">{{ __('Add Subcategory') }}</li>
                            </Link>
                            <Link :href="route('knowledge_base.create', {knowledge_base_category_id: category.id})">
                                <li class="p-2 hover:bg-gray-100 cursor-pointer">{{ __('Add Article') }}</li>
                            </Link>
                        </ul>
                    </div>
                    <!-- Parent Categories' knoledge base -->
                    <div>
                        <div v-for="knowledgebase in category.knowledgebases" :key="knowledgebase.id" class="ml-4">
                            <Link :href="route('knowledge_base.edit', knowledgebase.id)" class="">
                                <div class="flex items-center p-2 rounded hover:bg-gray-50 cursor-pointer">
                                    <div class="flex">
                                        <div class="pr-1">
                                            <DocumentTextIcon class="w-4 h-4 inline-block"/>
                                        </div>
                                        <div>{{ knowledgebase.title }}</div>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                    <!-- Child Categories -->
                    <div v-if="category.children" class="ml-4">
                        <div v-for="knowledge_base_category in category.children" :key="knowledge_base_category.id">
                            <div>
                                <div class="flex justify-between items-center p-2 rounded font-bold text-indigo-600">
                                    <div>
                                        {{ knowledge_base_category.name }} ({{ knowledge_base_category.knowledgebases.length }} {{ __('articles') }})
                                    </div>
                                    <EllipsisVerticalIcon class="w-7 h-7 cursor-pointer" @click.stop="toggle(knowledge_base_category.id)"/>
                                </div>
                                <!-- Dropdown Menu for Child Category -->
                                <div v-if="activeDropdown === knowledge_base_category.id" class="absolute right-4 mt-2 w-48 bg-white border rounded shadow-md z-10">
                                    <ul class="p-2">
                                        <Link :href="route('knowledge_base_categories.edit', knowledge_base_category.id)">
                                            <li class="p-2 hover:bg-gray-100 cursor-pointer">{{ __('Edit') }}</li>
                                        </Link>
                                        <Link :href="route('knowledge_base.create', {knowledge_base_category_id: knowledge_base_category.id})">
                                            <li class="p-2 hover:bg-gray-100 cursor-pointer">{{ __('Add Article') }}</li>
                                        </Link>
                                    </ul>
                                </div>
                            </div>
                            <!-- Child categories's knowlegde base -->
                            <div v-for="knowledgebase in knowledge_base_category.knowledgebases" :key="knowledgebase.id" class="ml-4">
                                <Link :href="route('knowledge_base.edit', knowledgebase.id)">
                                    <div class="flex items-center p-2 rounded hover:bg-gray-50 cursor-pointer">
                                        <div class="flex">
                                            <div class="pr-1">
                                                <DocumentTextIcon class="w-4 h-4 inline-block"/>
                                            </div>
                                            <div>{{ knowledgebase.title }}</div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- uncategorized knowledge base articles -->
                <div v-if="uncategorized_knowledge_bases.length > 0" class="p-4 bg-white rounded shadow">
                    <div class="text-lg font-bold text-gray-600">{{ __('Uncategorized') }}</div>
                    <div class="flex justify-between items-center p-2 rounded">
                        <div>
                            <div v-for="knowledgebase in uncategorized_knowledge_bases" :key="knowledgebase.id" class="ml-4">
                                <Link :href="route('knowledge_base.edit', knowledgebase.id)">
                                    <div class="flex items-center p-2 rounded hover:bg-gray-50 cursor-pointer">
                                        <div class="flex">
                                            <div class="pr-1">
                                                <DocumentTextIcon class="w-4 h-4 inline-block"/>
                                            </div>
                                            <div>{{ knowledgebase.title }}</div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import { Link, Head } from '@inertiajs/vue3'
import { FolderIcon, EllipsisVerticalIcon, ChevronRightIcon, DocumentTextIcon } from '@heroicons/vue/24/outline'

export default {
  metaInfo: { title: 'Knowledge Base Categories' },
  components: {
    Icon,
    Link,
    Head,
    EllipsisVerticalIcon,
    FolderIcon,
    ChevronRightIcon,
    DocumentTextIcon,
  },
  layout: Layout,
  props: {
    categories: Object,
    uncategorized_knowledge_bases: Array,
  },
  data() {
    return {
        activeDropdown: null
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(this.route('knowledge_base_categories.index'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    toggle(categoryId) {
        this.activeDropdown = this.activeDropdown === categoryId ? null : categoryId;
    },
    closeDropdown() {
        this.activeDropdown = null;
    },
    editCategory(categoryId) {
        this.$inertia.visit(this.route('knowledge_base_categories.edit', categoryId));
    },
    addSubcategory(categoryId) {
        this.$inertia.visit(this.route('knowledge_base_categories.create', { parent_id: categoryId }));
    },
    addArticle(categoryId) {
        this.$inertia.visit(this.route('knowledge_base.create', { category_id: categoryId }));
    }
  },
}
</script>